// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import { collection, query, where, orderBy, onSnapshot, getDocs } from "firebase/firestore";
import { startOfDay, endOfDay, subMinutes } from "date-fns";

function getDailyDeviceObservationsAggregationQuery(firestore, client_id, device_id, start_date, end_date) {
    /**
     * Get the daily aggregation data for a given device and date range
     * This query returns a document for each day in the date range.
     * The data then comes in the form of aggregation entries, which contain the aggregated data for each 5 minute interval.
     */
    // Calculate a broader query range
    // We'll query for documents that start up to 25 hours before end_date (assumng one doc roughly covers 24 hours)
    const extendedStartDate = new Date(start_date);
    extendedStartDate.setHours(extendedStartDate.getHours() - 25);
    
    return query(
        collection(firestore, "clients", client_id, "devices", device_id, "device_analytics", "observations_analytics", "aggregations_daily"),
        where("start_timestamp", ">=", extendedStartDate),
        where("start_timestamp", "<=", end_date),
        orderBy("start_timestamp", "asc")
    );
}

function getYearlyDeviceObservationsAggregationQuery(firestore, client_id, device_id, start_date, end_date) {
    /**
     * Get the yearly aggregation data for a given device and date range
     * This query returns a document for each year in the date range.
     * The data then comes in the form of aggregation entries, which contain the aggregated data for each day.
     */
    // Calculate a broader query range
    // We'll query for documents that start up to 1 year before end_date (assumng one doc roughly covers 1 year)
    const extendedStartDate = new Date(start_date);
    extendedStartDate.setFullYear(extendedStartDate.getFullYear() - 1);

    return query(
        collection(firestore, "clients", client_id, "devices", device_id, "device_analytics", "observations_analytics", "aggregations_yearly"),
        where("start_timestamp", ">=", extendedStartDate),
        where("start_timestamp", "<=", end_date),
        orderBy("start_timestamp", "asc")
    );
}

function subscribeToAggregatedDeviceObservationsData(
    firestore,
    clientId,
    deviceId,
    startDate,
    endDate,
    onUpdate,
    loadDetailed
) {
    /** 
     * Subscribe to live aggregated observations data for a given client, device and date range
     * Return all entries that overlap with the date range
     * Call onUpdate with the data when it changes
     * Return an unsubscribe function
    */
    const observationsDataQuery = loadDetailed ? getYearlyDeviceObservationsAggregationQuery(firestore, clientId, deviceId, startDate, endDate) : getDailyDeviceObservationsAggregationQuery(firestore, clientId, deviceId, startDate, endDate);

    const unsub = onSnapshot(observationsDataQuery, (snapshot) => {
        const observationsData = getOverlappingEntriesFromAggregationSnapshot(snapshot, startDate, endDate);
        onUpdate(observationsData);
    });

    return unsub;
}

export function subscribeToAggregatedSupplierObservationsData(
    firestore,
    clientId,
    supplierId,
    startDate,
    endDate,
    onUpdate    
) {
    /** 
     * Subscribe to live aggregated observations data for a given supplier and date range
     * Return all entries that overlap with the date range
     * Call onUpdate with the data when it changes
     * Return an unsubscribe function
    */
    
    // Calculate a broader query range
    // Load all documents that end after start_date. We'll filter out the ones that don't overlap with the date range in the callback    
    const collectionRef = collection(firestore, "clients", clientId, "client_collections", "suppliers", "supplier_details", supplierId, "supplier_analytics", "observations_analytics", "aggregations_yearly");
    let supplierDataQuery = query(collectionRef, orderBy("end_timestamp", "asc"));

    if (startDate) {
        supplierDataQuery = query(collectionRef, where("end_timestamp", ">=", startDate));
    }

    const unsub = onSnapshot(supplierDataQuery, (snapshot) => {
        const supplierData = getOverlappingEntriesFromAggregationSnapshot(snapshot, startDate, endDate);
        onUpdate(supplierData);
    });

    return unsub;
}

export async function getAggregatedSupplierObservationsData(
    firestore,
    clientId,
    supplierId,
    startDate,
    endDate
) {
    /**
     * Get the aggregated supplier observations data for a given supplier and date range
     * Return an array of entries that overlap with the date range
     * 
     * @param {Firestore} firestore - The firestore instance
     * @param {string} clientId - The client id
     * @param {string} supplierId - The supplier id
     * @param {Date} startDate - The start date of the date range
     * @param {Date} endDate - The end date of the date range
     * @returns {Array} An array of entries that overlap with the date range
    */
    const collectionRef = collection(firestore, "clients", clientId, "client_collections", "suppliers", "supplier_details", supplierId, "supplier_analytics", "observations_analytics", "aggregations_yearly");
    let supplierDataQuery = query(collectionRef, orderBy("end_timestamp", "asc"));

    if (startDate) {
        supplierDataQuery = query(collectionRef, where("end_timestamp", ">=", startDate));
    }

    const snapshot = await getDocs(supplierDataQuery);
    return getOverlappingEntriesFromAggregationSnapshot(snapshot, startDate, endDate);
}

function getOverlappingEntriesFromAggregationSnapshot(snapshot, startDate, endDate) {
    /**
     * Process the snapshot of aggregated observations data and return an array of entries that overlap with the date range
     * 
     * @param {QuerySnapshot} snapshot - The snapshot of the aggregated observations data
     * @param {Date} startDate - The start date of the date range
     * @param {Date} endDate - The end date of the date range
     * @returns {Array} An array of entries that overlap with the date range
    */
    let observationsData = [];
    snapshot.docs.forEach((doc) => {
        const data = doc.data();
        (data?.aggregation_entries || []).forEach((entry) => {
            entry["start_timestamp"] = entry["start_timestamp"].toDate();
            entry["end_timestamp"] = entry["end_timestamp"].toDate();
            
            // When no date filters are provided, include all entries
            // Otherwise check that entry overlaps with the date range
            if (!startDate && !endDate) {
                observationsData.push(entry);
            } else if (!startDate && endDate) {
                if (entry.end_timestamp <= endDate) {
                    observationsData.push(entry);
                }
            } else if (startDate && !endDate) {
                if (entry.start_timestamp >= startDate) {
                    observationsData.push(entry);
                }
            } else if (entry.start_timestamp <= endDate && entry.end_timestamp >= startDate) {
                observationsData.push(entry);
            }
        });
    });

    return observationsData;
}

function processAggregationEntries(aggregationEntries, startDate, endDate, timeIntervalMins) {
    /**
     * Process all loaded aggregation entries and parse them into the desired range and intervals.
     * Aggregation buckets of timeIntervalMins are created within the given date range.
     * Data entries that fall between different intervals are split and weighted accordingly.
     * 
     * @param {Array} aggregationEntries - The aggregation entries to process
     * @param {Date} startDate - The start date for the data loading
     * @param {Date} endDate - The end date for the data loading
     * @param {number} timeIntervalMins - The time interval in minutes of the aggregation buckets
     * @returns {Object} The data containing the aggregated data
     */
    const intervalMs = timeIntervalMins * 60 * 1000;
        
    // Create intervals from start_date to end_date
    const intervals = [];
    let currentIntervalStart;
    let endTime;

    if (startDate) {
        currentIntervalStart = startDate.getTime();
    } else {
        currentIntervalStart = aggregationEntries[0].start_timestamp.getTime();
    }
    
    if (endDate) {
        endTime = endDate.getTime();
    } else {
        endTime = aggregationEntries[aggregationEntries.length - 1].end_timestamp.getTime();
    }
    
    // Generate interval boundaries
    const emptyDataObject = getEmptyDataObjectForAggregationData(aggregationEntries);
    while (currentIntervalStart < endTime) {
        const intervalEnd = Math.min(currentIntervalStart + intervalMs, endTime);
        intervals.push({
            start: currentIntervalStart,
            end: intervalEnd,
            data: structuredClone(emptyDataObject)
        });
        currentIntervalStart = intervalEnd;
    }
    
    // Process each volume data entry - optimized for sorted volumeData
    let currentIntervalIndex = 0;
    
    aggregationEntries.forEach(entry => {
        const entryStartTime = new Date(entry.start_timestamp).getTime();
        const entryEndTime = new Date(entry.end_timestamp).getTime();
        const entryDuration = entryEndTime - entryStartTime;
        
        // Skip invalid entries
        if (entryDuration <= 0) return;
        
        // Find the first interval that could overlap with this entry
        // We can skip intervals that end before this entry starts
        while (
            currentIntervalIndex < intervals.length && 
            intervals[currentIntervalIndex].end < entryStartTime
        ) {
            currentIntervalIndex++;
        }
        
        // If we've gone through all intervals, we're done with this entry
        if (currentIntervalIndex >= intervals.length) return;
        
        // Process all intervals that overlap with this entry
        let i = currentIntervalIndex;
        while (i < intervals.length && intervals[i].start < entryEndTime) {
            const interval = intervals[i];
            
            // Find overlapping time range
            const overlapStart = Math.max(interval.start, entryStartTime);
            const overlapEnd = Math.min(interval.end, entryEndTime);
            const overlapDuration = overlapEnd - overlapStart;
            const overlapFraction = overlapDuration / entryDuration;
            
            // If there's an overlap, add the proportional value to this interval
            if (overlapFraction > 0) {
                updateIntervalWithEntry(interval.data, entry, overlapFraction);
            }
            
            i++;
            
            // If this interval starts after the entry ends, no need to check further intervals
            if (interval.start >= entryEndTime) break;
        }
    });

    // Set undefined entries
    setFutureEntriesUndefined(intervals);

    // Format the result
    const result = {
        intervals: intervals.map(interval => ({
            start_timestamp: new Date(interval.start),
            end_timestamp: new Date(interval.end),
            data: interval.data,
        })),
        reduced_data: reduceIntervals(intervals)
    };

    return result;
}

function getEmptyDataObjectForAggregationData(dataEntries) {
    /**
     * Get an empty data object for the aggregation data.
     * Initializes the data object with the correct structure and zero values.
     * Helps handle the case where no data is available for a given interval.
     * 
     * @param {Array} dataEntries - The data entries to process
     * @returns {Object} The empty data object
     */
    const emptyDataObject = {
        "data_per_labelset": {},
        "total_count": 0,
        "total_weight_kg": 0
    };

    // Initialize data_per_labelset
    dataEntries.forEach((entry) => {
        for (const [labelsetKey, labelsetValue] of Object.entries(entry?.data_per_labelset || {})) { 
            if (labelsetValue.numeric_label) {  // Handle numeric labels
                emptyDataObject["data_per_labelset"][labelsetKey] = {
                    "average": undefined,
                    "count": 0,
                    "sum": 0,
                    "numeric_label": true
                };
            } else {  // Handle categorical labels
                if (!(labelsetKey in emptyDataObject["data_per_labelset"])) {
                    emptyDataObject["data_per_labelset"][labelsetKey] = {};
                }

                for (const labelKey of Object.keys(labelsetValue?.label_counts || {})) {
                    if (!(labelKey in emptyDataObject["data_per_labelset"][labelsetKey])) {
                        emptyDataObject["data_per_labelset"][labelsetKey][labelKey] = {
                            "count": 0,
                            "detection_count": 0,
                            "detection_area_frac_sum": 0,
                        };
                    }
                }
            }
        }
    });

    // Initialize data_per_material
    if (emptyDataObject?.data_per_labelset?.materials) {
        const emptyDataWithoutMaterials = structuredClone(emptyDataObject);
        emptyDataObject["data_per_material"] = {};
        if ("materials" in emptyDataWithoutMaterials["data_per_labelset"]) {
            delete emptyDataWithoutMaterials["data_per_labelset"]["materials"];
        }

        for (const materialKey of Object.keys(emptyDataObject?.data_per_labelset?.materials || {})) {
            emptyDataObject["data_per_material"][materialKey] = structuredClone(emptyDataWithoutMaterials);
        }
    }

    return emptyDataObject
}

function updateIntervalWithEntry(interval_data, entry, overlapFraction) {
    /**
     * Update the interval data with the entry data.
     * 
     * @param {Object} interval_data - The interval data to update
     * @param {Object} entry - The entry data to update with
     * @param {number} overlapFraction - how much of the entry overlaps with the interval -> used to weight the data
     */
    function updateDataPerLabelset(data, entry, overlapFraction) {
        data["total_count"] += entry.total_count * overlapFraction;
        data["total_weight_kg"] += entry.total_weight_kg * overlapFraction;

        // Update data_per_labelset
        for (const [labelsetKey, labelsetValue] of Object.entries(entry?.data_per_labelset || {})) {
            if (!(labelsetKey in interval_data["data_per_labelset"])) {
                continue;
            }

            if (labelsetValue?.numeric_label) {  // Handle numeric labels
                data["data_per_labelset"][labelsetKey]["count"] += labelsetValue.count * overlapFraction;
                data["data_per_labelset"][labelsetKey]["sum"] += labelsetValue.sum * overlapFraction;

                if (data["data_per_labelset"][labelsetKey]["count"] > 0) {
                    data["data_per_labelset"][labelsetKey]["average"] = data["data_per_labelset"][labelsetKey]["sum"] / data["data_per_labelset"][labelsetKey]["count"];
                } else {
                    data["data_per_labelset"][labelsetKey]["average"] = undefined;
                }
            } else {  // Handle categorical labels
                for (const labelKey of Object.keys(labelsetValue?.label_counts || {})) {
                    data["data_per_labelset"][labelsetKey][labelKey]["count"] += labelsetValue.label_counts[labelKey] * overlapFraction;
                }
                for (const labelKey of Object.keys(labelsetValue?.detection_counts || {})) {
                    data["data_per_labelset"][labelsetKey][labelKey]["detection_count"] += labelsetValue.detection_counts[labelKey] * overlapFraction;
                }
                for (const labelKey of Object.keys(labelsetValue?.detection_area_frac_sums || {})) {
                    data["data_per_labelset"][labelsetKey][labelKey]["detection_area_frac_sum"] += labelsetValue.detection_area_frac_sums[labelKey] * overlapFraction;
                }
            }
        }
    }
    updateDataPerLabelset(interval_data, entry, overlapFraction);

    // Update data_per_material
    for (const [materialKey, dataPerMaterialKey] of Object.entries(entry?.data_per_material || {})) {
        updateDataPerLabelset(interval_data["data_per_material"][materialKey], dataPerMaterialKey, overlapFraction);
    }
}

function setFutureEntriesUndefined(intervals) {
    /**
     * Set properties of entries that are in the future to undefined as opposed to 0
        * 
        * @param {Object} data - The interval data to set undefined entries for
     */
    const now = new Date().getTime();
    intervals.forEach(interval => {
        if (interval.start <= now) {
            return;
        }

        for (const [labelsetKey, labelsetValue] of Object.entries(interval.data["data_per_labelset"])) {
            if (labelsetValue?.numeric_label) {
                interval.data["data_per_labelset"][labelsetKey]["average"] = undefined;
            } else {
                for (const [labelKey, labelValue] of Object.entries(labelsetValue)) {
                    labelValue["count"] = undefined;
                    labelValue["detection_count"] = undefined;
                    labelValue["detection_area_frac_sum"] = undefined;
                }
            }
        }
    });
}

// This function is not used anymore but temporarily kept in case it is needed in the future
function setUndefinedEntries(data) {
    /**
     * Set entries where there are no observations to undefined (as opposed to 0)
     * This mostly concerns detections as there should be a distinction between no detections and no data
     * 
     * @param {Object} data - The interval data to set undefined entries for
     */
    if (data.total_count > 0) {
        // The data is defined, so we don't need to set any undefined entries
        return;
    }

    for (const [labelsetKey, labelsetValue] of Object.entries(data["data_per_labelset"])) {
        if (labelsetValue?.numeric_label) {
            data["data_per_labelset"][labelsetKey]["average"] = undefined;
        } else {
            for (const [labelKey, labelValue] of Object.entries(labelsetValue)) {
                labelValue["count"] = undefined;
                labelValue["detection_count"] = undefined;
                labelValue["detection_area_frac_sum"] = undefined;
            }
        }
    }
}

function reduceIntervals(intervals) {
    /**
     * Create aggregations like sums and averages
     * 
     * @param {Array} intervals - The intervals to reduce
     * @returns {Object} The data containing the aggregated data
     */
    // Calculate the sum of all values in the object
    function sumObjectRecursively(sum, curr) {
        // Recursively sum up all values in the object
        for (const [key, value] of Object.entries(curr)) {
            if (typeof(value) === "object") {
                if (!(key in sum)) {
                    sum[key] = {};
                }
                sumObjectRecursively(sum[key], value);
            } else if (typeof(value) === "number") {
                if (!(key in sum)) {
                    sum[key] = 0;
                }
                sum[key] += value;
            }
        }
        return sum;
    }

    function calculateAverages(curr) {
        // Recursively calculate averages for all objects that have an average property
        if (typeof(curr) === "object" && "average" in curr) {
            curr["average"] = curr["sum"] / curr["count"];
        } else if (typeof(curr) === "object") {
            for (const [key, value] of Object.entries(curr)) {
                calculateAverages(value);
            }
        }
    }

    const reducedData = intervals.reduce((acc, interval) => {
        return sumObjectRecursively(acc, interval.data)
    }, {});
    calculateAverages(reducedData);

    return reducedData;
}

export function subscribeToAggregatedDeviceObservations(
    firestore,
    clientId,
    deviceId,
    startDate,
    endDate,
    timeIntervalMins,
    onUpdate
) {
    /**
     * Subscribe to live aggregated device observations data for a given device and date range.
     * Use the yearly aggregations (with daily entries) if the time-interval is >= 1 day and more than 31 days are loaded
     * Keep in mind that the yearly aggregations are only updated once a day, so the data might be slightly outdated
     * 
     * @param {Firestore} firestore - The firestore instance
     * @param {string} clientId - The client id
     * @param {string} deviceId - The device id
     * @param {Date} startDate - The start date for the data loading
     * @param {Date} endDate - The end date for the data loading
     * @param {number} timeIntervalMins - The time interval in minutes of the aggregation buckets
     * @param {Function} onUpdate - The function to call when the data is updated
     * @returns {Function} The unsubscribe function to unmout the subscription
     */
    const timeRangeDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    const loadDetailed = timeIntervalMins >= 1440 && timeRangeDays > 31;
    
    function processFunction(aggregationEntries) {
        const result = processAggregationEntries(aggregationEntries, startDate, endDate, timeIntervalMins);
        onUpdate(result);
    }

    return subscribeToAggregatedDeviceObservationsData(firestore, clientId, deviceId, startDate, endDate, processFunction, loadDetailed);
}

export function subscribeToAggregatedSupplierObservations(
    firestore,
    clientId,
    supplierId,
    startDate,
    endDate,
    timeIntervalMins,
    onUpdate
) {
    /**
     * Subscribe to live aggregated supplier observations data for a given supplier and date range.
     * 
     * @param {Firestore} firestore - The firestore instance
     * @param {string} clientId - The client id
     * @param {string} supplierId - The supplier id
     * @param {Date} startDate - The start date for the data loading
     * @param {Date} endDate - The end date for the data loading
     * @param {number} timeIntervalMins - The time interval in minutes of the aggregation buckets
     * @param {Function} onUpdate - The function to call when the data is updated
     * @returns {Function} The unsubscribe function to unmout the subscription
     */
    
    function processFunction(aggregationEntries) {
        const result = processAggregationEntries(aggregationEntries, startDate, endDate, timeIntervalMins);
        onUpdate(result);
    }

    return subscribeToAggregatedSupplierObservationsData(firestore, clientId, supplierId, startDate, endDate, processFunction);
}

export async function getAggregatedSupplierObservations(
    firestore,
    clientId,
    supplierId,
    startDate,
    endDate,
    timeIntervalMins
) {
    /**
     * Get the aggregated supplier observations data for a given supplier and date range.
     * 
     * @param {Firestore} firestore - The firestore instance
     * @param {string} clientId - The client id
     * @param {string} supplierId - The supplier id
     * @param {Date} startDate - The start date for the data loading
     * @param {Date} endDate - The end date for the data loading
     * @param {number} timeIntervalMins - The time interval in minutes of the aggregation buckets
     * @returns {Object} The data containing the aggregated data
     */
    
    const aggregationEntries = await getAggregatedSupplierObservationsData(firestore, clientId, supplierId, startDate, endDate);
    return processAggregationEntries(aggregationEntries, startDate, endDate, timeIntervalMins);
}

export async function filterOutAggregationsWithLessThanNObservations(aggregatedSupplierObservations, minObservations) {
    /**
     * Filter out aggregations with less than N observations.
     * Used to avoid displaying aggregations with insufficient data to make a meaningful claim.
     * 
     * @param {Object} aggregatedSupplierObservations - The aggregated supplier observations data
     * @param {number} minObservations - The minimum number of observations
     * @returns {Object} The filtered aggregated supplier observations data
     */
    if (aggregatedSupplierObservations?.reduced_data?.total_count < minObservations) {
        delete aggregatedSupplierObservations.reduced_data.data_per_labelset;
    }
    for (const [materialKey, dataPerMaterialKey] of Object.entries(aggregatedSupplierObservations?.reduced_data?.data_per_material || {})) {
        if (dataPerMaterialKey?.total_count < minObservations) {
            delete aggregatedSupplierObservations.reduced_data.data_per_material[materialKey]?.data_per_labelset;
        }
    }
}